/**
 * Renders a lesson drawing on paper. Static (up to a step) or animated via a per-stroke progress
 * matrix. Art lessons (`drawing.art`, docs/ART-BIBLE.md) are painted by the shared `Figure` renderer in
 * the active art style; drawings still waiting for art (`artStatus: 'pending'`) fall back to the legacy
 * pencil strokes (stroke-dashoffset with pathLength=1, so no measuring needed).
 */
import { memo, useMemo } from 'react';
import { artFrame, hasArt, isLightDrawing, stepRefs, type Drawing, type Scaffold, type Stroke, type StrokeTone } from '@sketchling/core';
import { WhenDrawingReady } from './WhenReady';
import { Figure } from '../art/Figure';
import { useArtStyle } from '../art/useArtStyle';
import { WORLD_ART, worldOf } from '../art/worlds';
import { Vignette, hasVignette } from '../art/vignette/Vignette';
import { ArtThumb } from '../art/Thumb';
import { artSig, drawingThumbKey } from '../art/thumbKeys';

/** Legacy line drawings, tinted to sit next to the Storybook art (warm ink, light-blue guides). */
export const TONE_STYLE: Record<StrokeTone, { stroke: string; width: number; opacity: number }> = {
  guide: { stroke: '#7DBDE8', width: 2, opacity: 0.9 },
  line: { stroke: '#4A3226', width: 3.2, opacity: 1 },
  detail: { stroke: '#4A3226', width: 2.2, opacity: 1 },
  shade: { stroke: '#8C7262', width: 1.4, opacity: 0.85 },
  accent: { stroke: '#E9783A', width: 3, opacity: 1 },
  erase: { stroke: 'none', width: 0, opacity: 0 },
  fill: { stroke: 'none', width: 0, opacity: 0 },
};
export const HIGHLIGHT = '#E9783A';
/** Lesson paper. */
export const PAPER = '#FFFAF0';

export interface DrawingViewProps {
  drawing: Drawing;
  /** Last step to show (inclusive). Defaults to the final step. */
  upTo?: number;
  /** First step to show. */
  from?: number;
  /** Per-step, per-stroke progress 0..1 (animation). Overrides upTo for the steps it covers (index = step index - from). */
  progress?: number[][];
  /** Steps drawn in the "current" highlight colour. */
  highlightStep?: number;
  /** Fade construction guides once the outline exists. */
  fadeGuides?: boolean;
  paper?: boolean;
  className?: string;
  title?: string;
  /** Extra SVG overlay (e.g. pencil cursor) rendered on top, in drawing coordinates. */
  overlay?: React.ReactNode;
  /** Optional affine transform for composition (studio). */
  transform?: readonly number[];
  /** v2 scaffold (art lessons): trace ghost / main shapes only / no guides. */
  scaffold?: Scaffold;
  /** Show the main shapes now ("Need a hint?"). */
  hint?: boolean;
}

function StrokePath({ s, p, highlight, faded }: { s: Stroke; p: number; highlight: boolean; faded: boolean }) {
  const tone = s.tone ?? 'line';
  const st = TONE_STYLE[tone];
  if (p <= 0 || !st.width) return null;
  return (
    <path
      d={s.d}
      fill="none"
      stroke={highlight && tone !== 'accent' && tone !== 'guide' ? HIGHLIGHT : st.stroke}
      strokeWidth={s.w ?? st.width}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={faded ? st.opacity * 0.35 : st.opacity}
      pathLength={1}
      strokeDasharray={p < 1 ? '1 1' : undefined}
      strokeDashoffset={p < 1 ? 1 - p : undefined}
    />
  );
}

/** Legacy stroke drawings only (art drawings render through `DrawingView` / `ArtDrawing`). */
export function DrawingStrokes({ drawing, upTo, from = 0, progress, highlightStep, fadeGuides = true }: Omit<DrawingViewProps, 'paper' | 'className' | 'title' | 'overlay'>) {
  const last = upTo ?? drawing.steps.length - 1;
  return (
    <>
      {drawing.steps.map((step, i) => {
        if (i < from || i > last) return null;
        const prog = progress?.[i - from];
        return (
          <g key={i} data-step={i}>
            {step.strokes.map((s, j) => (
              <StrokePath key={j} s={s} p={prog ? (prog[j] ?? 0) : 1} highlight={highlightStep === i} faded={fadeGuides && (s.tone ?? 'line') === 'guide' && i < last - 1} />
            ))}
          </g>
        );
      })}
    </>
  );
}

/** An art lesson at a step / animation frame, in the active art style. */
export function ArtDrawing({ drawing, upTo, from = 0, progress, highlightStep, paper = true, className = '', title, overlay, scaffold, hint }: DrawingViewProps) {
  const styleId = useArtStyle();
  const last = upTo ?? drawing.steps.length - 1;
  const frame = useMemo(() => artFrame(drawing, last, from, progress), [drawing, last, from, progress]);
  const hl = useMemo(() => (highlightStep !== undefined ? stepRefs(drawing, highlightStep) : undefined), [drawing, highlightStep]);
  return (
    <Figure
      def={drawing.art!}
      styleId={styleId}
      frame={frame}
      highlight={hl}
      className={`drawing-view art-drawing ${className}`}
      title={title}
      overlay={overlay}
      paper={paper ? PAPER : undefined}
      scaffold={scaffold}
      hint={hint}
      crop
      preserveAspectRatio="xMidYMid meet"
    />
  );
}

export const DrawingView = memo(function DrawingView(props: DrawingViewProps) {
  const { drawing, paper = true, className = '', title, overlay, transform } = props;
  if (isLightDrawing(drawing)) {
    // Light drawing (its world chunk is not loaded yet): the build-time thumbnail when it is the finished drawing,
    // else load the chunk and render once hydrated.
    const finished = hasArt(drawing) && !props.progress && props.highlightStep === undefined && !overlay && !transform && !props.scaffold && !props.hint && !props.from && props.upTo === undefined;
    if (finished) return <ArtDrawingThumb {...props} />;
    return <WhenDrawingReady drawing={drawing} fallback={<span className={`drawing-view thumb thumb-ph ${className}`} aria-hidden />}>{() => <DrawingView {...props} />}</WhenDrawingReady>;
  }
  if (drawing.art) {
    // The finished drawing (lists, cards, previews): a build-time thumbnail image instead of the live figure.
    const finished = !props.progress && props.highlightStep === undefined && !overlay && !transform && !props.scaffold && !props.hint && !props.from && (props.upTo === undefined || props.upTo >= drawing.steps.length - 1);
    if (finished) return <ArtDrawingThumb {...props} />;
    return <ArtDrawing {...props} />;
  }
  const [w, h] = drawing.viewBox;
  return (
    <svg className={`drawing-view ${className}`} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={title} preserveAspectRatio="xMidYMid meet">
      {paper && <rect x={0} y={0} width={w} height={h} fill={PAPER} />}
      <g transform={transform ? `matrix(${transform.join(' ')})` : undefined}>
        <DrawingStrokes {...props} />
      </g>
      {overlay}
    </svg>
  );
});

function ArtDrawingThumb(props: DrawingViewProps) {
  const styleId = useArtStyle();
  const { drawing, paper = true, className = '', title } = props;
  return (
    <ArtThumb
      thumbKey={drawingThumbKey(drawing.id)}
      sig={drawing.art ? artSig(drawing.art) : undefined}
      style={styleId}
      className={`drawing-view art-drawing ${className}`}
      paper={paper ? PAPER : undefined}
      alt={title}
      vector={() => (drawing.art ? <ArtDrawing {...props} /> : <WhenDrawingReady drawing={drawing}>{() => <ArtDrawing {...props} />}</WhenDrawingReady>)}
    />
  );
}

/** Thumbnail: the finished drawing (art lessons: full colour). */
export function DrawingThumb({ drawing, className, paper = true }: { drawing: Drawing; className?: string; paper?: boolean }) {
  return <DrawingView drawing={drawing} className={className} fadeGuides paper={paper} />;
}

/**
 * Card art: art lessons in full colour on their world's sky (Playground cards, path cards);
 * legacy drawings keep the paper.
 */
export function DrawingCardArt({ drawing, className = '', activityId }: { drawing: Drawing; className?: string; activityId?: string }) {
  const theme = WORLD_ART[worldOf(drawing)].theme;
  if (!hasArt(drawing)) return <DrawingView drawing={drawing} className={className} />;
  // Lesson vignette (subject in a small painted scene, art/vignette) when the world has one.
  if (hasVignette(drawing, activityId))
    return (
      <div className={`art-card ${className}`}>
        <Vignette drawing={drawing} activityId={activityId} />
      </div>
    );
  return (
    <div className={`art-card ${className}`} style={{ background: `radial-gradient(circle at 50% 38%, ${theme.skyLow} 0, ${theme.card.bg} 62%, ${theme.card.border} 100%)` }}>
      <DrawingView drawing={drawing} paper={false} />
    </div>
  );
}
